/**
 * Session context.
 */
import React from 'react'
import Amplify, { Hub } from '@aws-amplify/core'
import Auth from '@aws-amplify/auth'
import config from 'react-global-configuration'
import { Authenticator, Greetings } from 'aws-amplify-react'
import { apiFetchWithoutToken, apiFetchWithAccessToken } from './lib/api-fetch'

// Must be called to initialize Amplify.
export function configureSession(cognitoUserPoolId: string, cognitoClientId: string) {
  /*
  if (typeof window !== 'undefined') {
    (window as any).LOG_LEVEL = 'DEBUG'
  }
  */
  Amplify.configure({
    Auth: {
      region: 'eu-west-1',
      userPoolId: cognitoUserPoolId,
      userPoolWebClientId: cognitoClientId,
    },
  })
}

export const SessionContext = React.createContext(null)
export const SessionConsumer = SessionContext.Consumer

interface SessionStoreProps {
  children: any
}

export interface SessionContext {
  // Session management
  state: string
  signOut: Function
  getCurrentAuthenticatedUser: Function
  getCurrentSession: Function
  getCurrentAccountId: Function
  currentAccountId: string

  // User management
  fetchMe: Function
  updateMe: Function
  me?: object
  loadingMe: boolean
  changePassword: Function
  changeEmail: Function
  verifyEmail: Function
  deleteUser: Function

  // Account management
  fetchAccount: Function
  fetchAccountMembers: Function
  updateAccountMember: Function
  deleteAccountMember: Function
  fetchAccounts: Function
  updateAccount: Function
  deleteAccount: Function
  account?: object
  accountMembers: object[]
  accounts: object[]
  loadingAccount: boolean
  loadingAccounts: boolean
}

export class SessionStore extends React.Component<SessionStoreProps> {
  state = {
    // Session management
    state: 'signedOut',
    currentAccountId: '',
    // User management
    me: undefined,
    // Account management
    account: undefined,
    accountMembers: [],
    accounts: [],
    loadingAccount: false,
    loadingAccounts: false,
  }

  async setStateAsync(newState: any) {
    return new Promise((resolve) => {
      this.setState(newState, () => {
        resolve()
      })
    })
  }

  // Session management

  signOut = async () => {
    return Auth.signOut()
  }

  getCurrentAuthenticatedUser = async () => {
    return Auth.currentAuthenticatedUser()
  }

  getCurrentSession = async () => {
    return Auth.currentSession()
  }

  getCurrentAccountId = async () => {
    const session = await this.getCurrentSession()
    const idToken = session.getIdToken()
    const accountId = (idToken && idToken.payload['custom:accountId']) || null
    if (!accountId) throw new Error('No active accountId')
    if (accountId !== this.state.currentAccountId) {
      this.setState({
        currentAccountId: accountId,
      })
    }
    return accountId
  }

  fetchCurrentAccount = async () => {
    const accountId = await this.getCurrentAccountId()
    return this.fetchAccount(accountId)
  }

  // User management

  fetchMe = async () => {
    await this.setStateAsync({
      loadingMe: true,
    })
    try {
      const response = await apiFetchWithAccessToken('GET', config.get('ACCOUNT_API_URL') + '/me')
      this.setState({
        me: response,
        loadingMe: false,
      })
    } catch (err) {
      this.setStateAsync({
        loadingMe: false,
      })
    }
  }

  updateMe = async (newMe: any) => {
    await this.setStateAsync({
      loadingMe: true,
    })
    try {
      const response = await apiFetchWithAccessToken('PUT', config.get('ACCOUNT_API_URL') + '/me', newMe)
      this.setState({
        me: response,
        loadingMe: false,
      })
      const session = await this.getCurrentSession()
      const refreshToken = session.getRefreshToken()
      const user = await this.getCurrentAuthenticatedUser()
      await new Promise((resolve, reject) => {
        user.refreshSession(refreshToken, (err: any, result: any) => {
          if (err) reject(err)
          else resolve(result)
        })
      })
      // At this point the current ID token contains the updated accountId.
      const accountId = await this.getCurrentAccountId()
      this.setState({
        currentAccountId: accountId,
      })
    } catch (err) {
      this.setStateAsync({
        loadingMe: false,
      })
      throw err
    }
  }

  changePassword = async (oldPassword: string, newPassword: string) => {
    const user = await Auth.currentAuthenticatedUser()
    if (!user) {
      // Not signed in
      throw new Error('Not signed in')
    }

    const result = await new Promise((resolve, reject) => {
      user.changePassword(oldPassword, newPassword, (err: any, result: any) => {
        if (err) reject(err)
        else resolve(result)
      })
    })
    console.log('Change password result:', result)
  }

  changeEmail = async (newEmail: string) => {
    const user = await Auth.currentAuthenticatedUser()
    if (!user) {
      // Not signed in
      throw new Error('Not signed in')
    }

    const attributeEmail = { // new CognitoUserAttribute({
      Name: 'email',
      Value: newEmail,
    }

    const result = await new Promise((resolve, reject) => {
      user.updateAttributes([attributeEmail], (err: any, result: any) => {
        if (err) reject(err)
        else resolve(result)
      })
    })
    console.log('Change email result:', result)
  }

  verifyEmail = async (verificationCode: string) => {
    const user = await Auth.currentAuthenticatedUser()
    if (!user) {
      // Not signed in
      throw new Error('Not signed in')
    }

    const result = await new Promise((resolve, reject) => {
      user.verifyAttribute('email', verificationCode, {
        onSuccess: (result: any) => {
          // console.log('[Session] Successfully verified email:', result)
          return resolve(result)
        },
        onFailure: (err: any) => {
          // console.log('[Session] Error verifying email:', err)
          reject(err)
        },
      })
    })
    console.log('Verify email result:', result)
  }

  deleteUser = async () => {
    const user = await Auth.currentAuthenticatedUser()
    if (!user) {
      // Not signed in
      throw new Error('Not signed in')
    }
    const userId = user.getUsername()
    const isAutoSignUp = (userId === localStorage.getItem('cloudenAutoSignUpUsername'))
    console.log('Deleting user', userId, '(auto-signup: ' + isAutoSignUp + ')')
    const result = await new Promise((resolve, reject) => {
      user.deleteUser((err: any, result: any) => {
        if (err) reject(err)
        else resolve(result)
      })
    })
    console.log('User delete result:', result)
    // Sign out the session
    console.log('Signing out...')
    user.signOut()
    // If this was an auto-signup account, remove from local storage
    if (isAutoSignUp) {
      console.log('Removing auto-signup account information from localstorage...')
      localStorage.removeItem('cloudenAutoSignUpDone')
      localStorage.removeItem('cloudenAutoSignUpUsername')
      localStorage.removeItem('cloudenAutoSignUpEmail')
      localStorage.removeItem('cloudenAutoSignUpEmailChanged')
      localStorage.removeItem('cloudenAutoSignUpPassword')
      localStorage.removeItem('cloudenAutoSignUpPasswordChanged')
    }
    // Inform the system that the user has been deleted, so it can clean up
    await apiFetchWithoutToken('POST', config.get('ACCOUNT_API_URL') + '/complete-user-delete', { userId: userId })
  }

  // Account management

  fetchAccount = async (accountId: string) => {
    const response = await apiFetchWithAccessToken('GET', config.get('ACCOUNT_API_URL') + '/accounts/' + encodeURIComponent(accountId))
    this.setState({
      account: response,
    })
    return response
  }

  fetchAccountMembers = async (accountId: string) => {
    const response = await apiFetchWithAccessToken('GET', config.get('ACCOUNT_API_URL') + '/accounts/' + encodeURIComponent(accountId) + '/members')
    this.setState({
      accountMembers: response,
    })
    return response
  }

  updateAccountMember = async (accountId: string, memberUserId: string, membership: any) => {
    const response = await apiFetchWithAccessToken('PUT', config.get('ACCOUNT_API_URL') + '/accounts/' + encodeURIComponent(accountId) + '/members/' + memberUserId, membership)
    return response
  }

  deleteAccountMember = async (accountId: string, memberUserId: string) => {
    const response = await apiFetchWithAccessToken('DELETE', config.get('ACCOUNT_API_URL') + '/accounts/' + encodeURIComponent(accountId) + '/members/' + memberUserId)
    return response
  }

  fetchAccounts = async () => {
    const response = await apiFetchWithAccessToken('GET', config.get('ACCOUNT_API_URL') + '/accounts')
    this.setState({
      accounts: response,
    })
    return response
  }

  updateAccount = async (accountId: string, account: any) => {
    const response = await apiFetchWithAccessToken('PUT', config.get('ACCOUNT_API_URL') + '/accounts/' + encodeURIComponent(accountId), account)
    this.setState({
      account: response,
    })
    return response
  }

  deleteAccount = async (accountId: string) => {
    const response = await apiFetchWithAccessToken('DELETE', config.get('ACCOUNT_API_URL') + '/accounts/' + encodeURIComponent(accountId))
    return response
  }

  hubListener = (data: any) => {
    console.log('Hub event:', data)
    switch (data.payload.event) {
      case 'signIn':
        this.setState({
          state: 'signedIn',
        }, async () => {
          await this.getCurrentAccountId()
        })
        break
      case 'signUp':
        this.setState({
          state: 'signedUp',
        })
        break
      case 'signOut':
        this.setState({
          state: 'signedOut',
        })
        break
    }
  }

  async componentDidMount() {
    // Backwards compatibitility
    // @ts-ignore
    this.hubListener.onHubCapsule = this.hubListener
    Hub.listen('auth', this.hubListener, 'sessionContext')
    try {
      const session = await Auth.currentSession()
      if (session) {
        this.setState({
          state: 'signedIn'
        })
      }
    } catch (err) {
      console.log('Auth error:', err)
    }
  }

  async componentWillUnmount() {
    Hub.remove('auth', this.hubListener, 'sessionContext')
  }

  render() {
    return (
      <SessionContext.Provider value={{ ...this.state,
        // Session management
        signOut: this.signOut,
        getCurrentAuthenticatedUser: this.getCurrentAuthenticatedUser,
        getCurrentSession: this.getCurrentSession,
        getCurrentAccountId: this.getCurrentAccountId,
        fetchCurrentAccount: this.fetchCurrentAccount,
        // User management
        fetchMe: this.fetchMe,
        updateMe: this.updateMe,
        changePassword: this.changePassword,
        changeEmail: this.changeEmail,
        verifyEmail: this.verifyEmail,
        deleteUser: this.deleteUser,
        // Account management
        fetchAccount: this.fetchAccount,
        fetchAccountMembers: this.fetchAccountMembers,
        updateAccountMember: this.updateAccountMember,
        deleteAccountMember: this.deleteAccountMember,
        fetchAccounts: this.fetchAccounts,
        updateAccount: this.updateAccount,
        deleteAccount: this.deleteAccount,
      }}>
        {this.props.children}
      </SessionContext.Provider>
    )
  }
}

interface AuthenticatedViewProps {
  children: any
  sessionContext: SessionContext
  authState?: any
  authData?: any
  // onStateChange: Function
}

class AuthenticatedView extends React.Component<AuthenticatedViewProps> {
  render() {
    if (this.props.authState !== 'signedIn') {
      return null
    } else {
      // Signed in so we can render the children
      // console.log('Rendering AuthenticatedView')
      return React.cloneElement(this.props.children, {
        sessionContext: this.props.sessionContext,
      })
    }
  }
}

interface UnauthenticatedViewProps {
  authState?: any
  authData?: any
}

class UnauthenticatedView extends React.Component<UnauthenticatedViewProps> {
  render() {
    if (this.props.authState == 'signedIn') {
      return null
    } else {
      // Not yet signed in
      // console.log('Rendering UnauthenticatedView')
      return (
        <div className="container">
        </div>
      )
    }
  }
}

interface SessionAuthenticatorProps {
  children: React.ReactElement<any>
}

export class SessionAuthenticator extends React.Component<SessionAuthenticatorProps> {
  render() {
    const children = this.props.children
    return (
      <SessionContext.Consumer>
        {(sessionContext: SessionContext) => {
          return (
            <Authenticator sessionContext={sessionContext} hide={[Greetings]} signUpConfig={{
              hiddenDefaults: ['username', 'phone_number', 'email'],
              signUpFields: [{
                label: 'Email',
                key: 'username',
                required: true,
                placeholder: 'Email',
                type: 'email',
                displayOrder: 1
              }, {
                label: 'Password',
                key: 'password',
                required: true,
                placeholder: 'Password',
                type: 'password',
                displayOrder: 2,
              }]
            }}>
              <AuthenticatedView children={children} sessionContext={sessionContext} />
              <UnauthenticatedView />
            </Authenticator>
          )
        }}
      </SessionContext.Consumer>
    )
  }
}

export function withSessionContext(Comp: any) {
  return (props: any) => (
    <SessionContext.Consumer>
      {(sessionContext: SessionContext) => <Comp {...props} sessionContext={{...sessionContext}}/>}
    </SessionContext.Consumer>
  )
}
